import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { WidgetFrame } from "@/dashboard/WidgetFrame";
import { useUserTimeZone } from "@/utils/datetime";
import { formatDateInTz, formatDateTimeInTz } from "@/design-system/lib/format";
import { zoneCaption } from "@/lib/appointmentZones";
import { appointmentDayZone } from "../lib/tz";
import { useUpcomingAppointments } from "../hooks/useAppointments";

interface Props {
  limit?: number;
}

/**
 * Calendar Appointments v1.0 — the next N upcoming appointments for the
 * current user (owner or attendee), server-ordered by start time. Rendered
 * in the dashboard's personal band; mirrors <c>MyOpenTasksWidget</c>.
 */
export function UpcomingAppointmentsWidget({ limit = 5 }: Props) {
  const tz = useUserTimeZone();
  const q = useUpcomingAppointments(limit);

  // A rolling "next N" list has no single date, so the caption is name-only.
  const caption = zoneCaption(tz, null);

  return (
    <WidgetFrame
      title="Upcoming"
      to="/calendar"
      isLoading={q.isLoading}
      isError={q.isError}
      isEmpty={q.data?.length === 0}
      emptyText="Nothing scheduled."
    >
      {q.data && q.data.length > 0 && caption && (
        <p className="text-caption text-gray-mid m-0 mb-space-2">{caption}</p>
      )}
      {q.data && q.data.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {q.data.map((a) => (
            <li key={a.id}>
              <Link
                to="/calendar"
                className="flex items-center justify-between gap-space-2 rounded-md p-space-2 -mx-space-2 hover:bg-white-warm"
              >
                <div className="flex items-start gap-space-2 min-w-0">
                  <CalendarClock
                    size={16}
                    strokeWidth={1.5}
                    className="text-gold-shadow mt-[2px] shrink-0"
                    aria-hidden
                  />
                  <span className="text-small text-onyx truncate">{a.title}</span>
                </div>
                <span className="text-caption text-gray-mid shrink-0 whitespace-nowrap">
                  {a.isAllDay
                    ? // A wall-calendar day, read in the zone it was written in.
                      // Rendering it in the viewer's zone put it a day early for
                      // every viewer west of the author.
                      formatDateInTz(a.startAt, appointmentDayZone(a, tz), {
                        month: "short",
                        day: "numeric",
                      })
                    : formatDateTimeInTz(a.startAt, tz, {
                        month: "short",
                        day: "numeric",
                      })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WidgetFrame>
  );
}
